import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Card, Alert, Spinner, Button, Badge } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ordersAPI } from '../utils/api';
import OrderForm from '../components/OrderForm';

const OrderEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  // Check if user is HQ (staff/superuser) - only HQ can edit prices
  const isHQ = user?.isStaff || user?.isSuperuser;

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [initialSelectedMaterials, setInitialSelectedMaterials] = useState([]);
  const [initialNote, setInitialNote] = useState('');
  const [initialDiscountVat, setInitialDiscountVat] = useState(0);
  const [initialDiscountNonVat, setInitialDiscountNonVat] = useState(0);

  // Ref to prevent duplicate API calls for the same order ID
  const loadedIdRef = useRef(null);

  useEffect(() => {
    if (loadedIdRef.current !== id) {
      loadedIdRef.current = id;
      loadOrder();
    }
  }, [id]);

  const loadOrder = async () => {
    try {
      setLoading(true);
      const res = await ordersAPI.getById(id);
      const orderData = res.data.data;

      setOrder(orderData);
      setInitialNote(orderData.note || '');
      setInitialDiscountVat(orderData.discountVat || 0);
      setInitialDiscountNonVat(orderData.discountNonVat || 0);

      // Map order items to selected materials
      const materials = orderData.items.map(item => ({
        materialId: item.material.id,
        materialCode: item.material.code,
        materialName: item.material.name,
        unit: item.material.unit,
        quantity: item.quantity,
        unitCost: item.unitCost,
        note: item.note || '',
        hasVat: item.hasVat || false
      }));

      setInitialSelectedMaterials(materials);
    } catch (err) {
      setError(err.response?.data?.message || 'เกิดข้อผิดพลาดในการโหลดข้อมูล');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (formData) => {
    setError('');

    if (formData.selectedMaterials.length === 0) {
      setError('กรุณาเพิ่มวัตถุดิบอย่างน้อย 1 รายการ');
      return;
    }

    const hasInvalidQty = formData.selectedMaterials.some(item => !(parseFloat(item.quantity) > 0));
    if (hasInvalidQty) {
      setError('กรุณากรอกจำนวนให้ครบทุกรายการ (ต้องมากกว่า 0)');
      return;
    }

    setSaving(true);

    try {
      const orderData = {
        note: formData.note,
        discountVat: formData.discountVat || 0,
        discountNonVat: formData.discountNonVat || 0,
        items: formData.selectedMaterials.map(item => ({
          materialId: item.materialId,
          quantity: item.quantity,
          unitCost: item.unitCost,
          note: item.note,
          hasVat: item.hasVat || false
        }))
      };

      // If order was rejected, reset to pending status when resubmitting
      if (order.approvalStatus === 'rejected') {
        orderData.approvalStatus = 'pending';
      }

      await ordersAPI.update(id, orderData);

      if (order.approvalStatus === 'rejected') {
        alert('แก้ไขและส่งคำสั่งซื้อกลับเข้าสู่ระบบสำเร็จ (สถานะ: รออนุมัติ)');
      } else {
        alert('อัพเดทคำสั่งซื้อสำเร็จ');
      }

      navigate('/');
    } catch (err) {
      setError(err.response?.data?.message || 'เกิดข้อผิดพลาดในการอัพเดทคำสั่งซื้อ');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container className="main-content text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">กำลังโหลดข้อมูล...</p>
      </Container>
    );
  }

  if (!order) {
    return (
      <Container className="main-content">
        <Alert variant="danger">ไม่พบคำสั่งซื้อที่ต้องการแก้ไข</Alert>
        <Button variant="outline-secondary" onClick={() => navigate('/')}>
          <i className="bi bi-arrow-left me-2"></i>
          กลับ
        </Button>
      </Container>
    );
  }

  return (
    <Container fluid className="main-content">
      <Row className="mb-4">
        <Col>
          <h2>
            <i className={order.approvalStatus === 'rejected' ? 'bi bi-arrow-clockwise me-2' : 'bi bi-pencil me-2'}></i>
            {order.approvalStatus === 'rejected' ? `แก้ไขและส่งคำสั่งซื้อใหม่ #${id}` : `แก้ไขคำขอสั่งซื้อ #${id}`}
          </h2>
          <p className="text-muted">
            {order.approvalStatus === 'rejected'
              ? 'แก้ไขรายการตามเหตุผลที่ถูกปฏิเสธและส่งกลับเข้าสู่ระบบใหม่'
              : 'แก้ไขรายการวัตถุดิบและจำนวนที่ต้องการ'}
          </p>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Show rejection reason if order was rejected */}
      {order.approvalStatus === 'rejected' && order.note && (
        <Alert variant="warning">
          <Alert.Heading>
            <i className="bi bi-exclamation-triangle-fill me-2"></i>
            คำสั่งซื้อนี้ถูกปฏิเสธ
          </Alert.Heading>
          <p className="mb-0">
            <strong>เหตุผล:</strong> {order.note}
          </p>
          <hr />
          <p className="mb-0 text-muted small">
            <i className="bi bi-info-circle me-1"></i>
            คุณสามารถแก้ไขคำสั่งซื้อและส่งกลับเข้าสู่ระบบอีกครั้ง (สถานะจะเปลี่ยนเป็น "รออนุมัติ")
          </p>
        </Alert>
      )}

      {/* Order Info */}
      <Card className="mb-3">
        <Card.Body>
          <Row>
            <Col md={4}>
              <p className="mb-1">
                <strong>ผู้สั่ง:</strong> {order.orderedBy?.fullName || order.orderedBy?.username}
              </p>
              <p className="mb-1">
                <strong>สร้างเมื่อ:</strong> {new Date(order.createdAt).toLocaleString('th-TH')}
              </p>
            </Col>
            <Col md={4}>
              <p className="mb-1">
                <strong>สถานะปัจจุบัน:</strong>{' '}
                {order.approvalStatus === 'approved' && <Badge bg="success">อนุมัติแล้ว</Badge>}
                {order.approvalStatus === 'pending' && <Badge bg="warning" text="dark">รออนุมัติ</Badge>}
                {order.approvalStatus === 'rejected' && <Badge bg="danger">ปฏิเสธ</Badge>}
              </p>
            </Col>
            <Col md={4}>
              {order.approvedBy && (
                <p className="mb-1">
                  <strong>ผู้อนุมัติ:</strong> {order.approvedBy.fullName || order.approvedBy.username}
                </p>
              )}
              {order.approvedAt && (
                <p className="mb-1">
                  <strong>อนุมัติเมื่อ:</strong> {new Date(order.approvedAt).toLocaleString('th-TH')}
                </p>
              )}
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Row>
        <Col md={12}>
          {order.fulfillmentStatus === 'fulfilled' && (
            <Alert variant="info" className="mb-3">
              <i className="bi bi-lock-fill me-2"></i>
              <strong>คำสั่งซื้อนี้เบิกสินค้าแล้ว</strong> — สามารถแก้ไขได้เฉพาะราคาและส่วนลดเท่านั้น จำนวนและรายการสินค้าถูกล็อก
            </Alert>
          )}
          <OrderForm
            initialSelectedMaterials={initialSelectedMaterials}
            initialNote={initialNote}
            initialDiscountVat={initialDiscountVat}
            initialDiscountNonVat={initialDiscountNonVat}
            onSubmit={handleSubmit}
            loading={saving}
            submitButtonText={order.approvalStatus === 'rejected' ? 'แก้ไขและส่งกลับเข้าสู่ระบบ' : 'บันทึกการแก้ไข'}
            submitButtonIcon={order.approvalStatus === 'rejected' ? 'bi-arrow-clockwise' : 'bi-save'}
            allowPriceEdit={isHQ}
            allowQuantityEdit={order.fulfillmentStatus !== 'fulfilled'}
          />

          <div className="d-grid gap-2 mb-4">
            <Button
              type="button"
              variant="outline-secondary"
              onClick={() => navigate('/')}
            >
              <i className="bi bi-x-circle me-2"></i>
              ยกเลิก
            </Button>
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default OrderEdit;
